import {
  Archive, ArrowLeft, Bold, CalendarDays, Check, CircleAlert, Code, FilePenLine,
  Folder, Forward, Inbox, Italic, Link, List, ListOrdered, LoaderCircle, LogOut,
  Mail, MailOpen, Paperclip, PenLine, Quote, RefreshCw, RemoveFormatting, Reply,
  ReplyAll, Search, Send, SendHorizontal, Settings, ShieldAlert, Sparkles, Star,
  Strikethrough, Trash2, TriangleAlert, Underline, Users, X,
  type LucideIcon,
} from "lucide-react";

/**
 * Icons.
 *
 * The design marks these up as `<i data-lucide="name">` and calls
 * `lucide.createIcons()` from a CDN script, which replaces each `<i>` with an
 * `<svg>`. That is wrong here twice over: it mutates nodes React owns (so a
 * re-render reconciles against elements that are no longer what React rendered,
 * and icons drop out), and it made every page load depend on
 * `unpkg.com/lucide@latest` — unpinned, and dead icons if unpkg is.
 *
 * lucide-react renders the same glyphs as real components, so React owns the
 * whole tree and nothing is fetched at runtime. The class name stays `.lucide`
 * because mail.css sizes icons through it.
 */
const ICONS: Record<string, LucideIcon> = {
  archive: Archive,
  "arrow-left": ArrowLeft,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strikethrough: Strikethrough,
  list: List,
  "list-ordered": ListOrdered,
  quote: Quote,
  code: Code,
  "remove-formatting": RemoveFormatting,
  link: Link,
  "calendar-days": CalendarDays,
  check: Check,
  "circle-alert": CircleAlert,
  "file-pen-line": FilePenLine,
  folder: Folder,
  forward: Forward,
  inbox: Inbox,
  loader: LoaderCircle,
  "log-out": LogOut,
  mail: Mail,
  "mail-open": MailOpen,
  paperclip: Paperclip,
  "pen-line": PenLine,
  "refresh-cw": RefreshCw,
  reply: Reply,
  "reply-all": ReplyAll,
  search: Search,
  send: Send,
  "send-horizontal": SendHorizontal,
  settings: Settings,
  "shield-alert": ShieldAlert,
  sparkles: Sparkles,
  star: Star,
  "trash-2": Trash2,
  "triangle-alert": TriangleAlert,
  users: Users,
  x: X,
};

export function Icon({ name, className }: { name: string; className?: string }) {
  const Glyph = ICONS[name] ?? Folder;
  // aria-hidden throughout: every icon in this app sits inside a control that
  // already carries a title or visible label, so announcing it would duplicate.
  return <Glyph className={className ? `lucide ${className}` : "lucide"} aria-hidden="true" />;
}
